import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Error Boundary component to catch JavaScript errors anywhere in the component tree
 * Provides a fallback UI and error logging
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error: error,
            errorInfo: errorInfo,
        });

        // You could also log the error to an error reporting service here
        // logErrorToService(error, errorInfo);
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Fallback UI
            return (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: '#0a0a0a',
                        color: '#ff0000',
                        fontFamily: '"Courier New", Courier, monospace',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px',
                    }}
                >
                    <ErrorOutlineIcon
                        sx={{
                            fontSize: '80px',
                            color: '#ff0000',
                            marginBottom: '20px',
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }}
                    />

                    <Typography
                        sx={{
                            fontFamily: 'inherit',
                            fontSize: '24px',
                            color: '#ff0000',
                            marginBottom: '10px',
                            textAlign: 'center',
                        }}
                    >
                        {'>'} CRITICAL ERROR
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: 'inherit',
                            fontSize: '14px',
                            color: '#ff6666',
                            marginBottom: '30px',
                            textAlign: 'center',
                            maxWidth: '600px',
                        }}
                    >
                        The application encountered an unexpected error and needs to recover.
                    </Typography>

                    {this.state.error && (
                        <Box
                            sx={{
                                backgroundColor: '#1a0000',
                                border: '1px solid #ff0000',
                                borderRadius: '4px',
                                padding: '15px',
                                marginBottom: '20px',
                                maxWidth: '800px',
                                width: '100%',
                                maxHeight: '200px',
                                overflow: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                    backgroundColor: '#0a0a0a',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#ff0000',
                                    borderRadius: '4px',
                                },
                            }}
                        >
                            <Typography
                                component="pre"
                                sx={{
                                    fontFamily: 'inherit',
                                    fontSize: '12px',
                                    color: '#ff6666',
                                    margin: 0,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {this.state.error.toString()}
                                {this.state.errorInfo && '\n\n'}
                                {this.state.errorInfo?.componentStack}
                            </Typography>
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            marginTop: '10px',
                        }}
                    >
                        <Button
                            onClick={this.handleReset}
                            startIcon={<RefreshIcon />}
                            sx={{
                                color: '#ffaa00',
                                border: '1px solid #ffaa00',
                                fontFamily: 'inherit',
                                fontSize: '14px',
                                padding: '8px 20px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 170, 0, 0.2)',
                                    borderColor: '#ffaa00',
                                },
                            }}
                        >
                            Try Again
                        </Button>

                        <Button
                            onClick={this.handleReload}
                            startIcon={<RefreshIcon />}
                            sx={{
                                color: '#00ff00',
                                border: '1px solid #00ff00',
                                fontFamily: 'inherit',
                                fontSize: '14px',
                                padding: '8px 20px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 255, 0, 0.2)',
                                    borderColor: '#00ff00',
                                },
                            }}
                        >
                            Reload Page
                        </Button>
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: 'inherit',
                            fontSize: '11px',
                            color: '#664444',
                            marginTop: '30px',
                            textAlign: 'center',
                        }}
                    >
                        If this error persists, please check the browser console for more details.
                    </Typography>
                </Box>
            );
        }

        return this.props.children;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
